import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../ui/LoadingSpinner';

interface SalaryRecord {
  id: string;
  teamMember: {
    id: string;
    name: string;
    displayName?: string;
    email: string;
    role: string;
  };
  gym: {
    id: string;
    name: string;
    displayName: string;
  };
  baseSalary: number;
  currency: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  workingHoursPerDay?: number | null;
  workingDaysPerMonth?: number | null;
  overtimeRate?: number | null;
  undertimeDeductionRate?: number | null;
  createdAt: string;
}

interface TeamSalaryTabProps {
  teamMembers: Array<{ id: string; userId: string; name: string; displayName?: string; role: string }>;
}

const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];

const emptyForm = {
  userGymMappingId: '',
  baseSalary: '',
  currency: 'INR',
  effectiveFrom: new Date().toISOString().split('T')[0],
  effectiveTo: '',
  workingHoursPerDay: '8',
  workingDaysPerMonth: '26',
  overtimeRate: '',
  undertimeDeductionRate: ''
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  trainer: 'Trainer',
  staff: 'Staff'
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700',
  trainer: 'bg-blue-100 text-blue-700',
  staff: 'bg-green-100 text-green-700'
};

export default function TeamSalaryTab({ teamMembers }: TeamSalaryTabProps) {
  const [salaries, setSalaries] = useState<SalaryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyMember, setHistoryMember] = useState<{ id: string; name: string } | null>(null);
  const [memberHistory, setMemberHistory] = useState<SalaryRecord[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingMember, setEditingMember] = useState<{ id: string; name: string; role: string } | null>(null);

  const fetchSalaries = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${API_BASE_URL}/api/team-salaries`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSalaries(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching salaries:', error);
      toast.error('Failed to load salary data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const openSetSalary = (member: { id: string; name: string; role: string }) => {
    const current = getCurrentSalary(member.id);
    setEditingMember(member);
    setFormData({
      ...emptyForm,
      userGymMappingId: member.id,
      baseSalary: current?.baseSalary?.toString() || '',
      currency: current?.currency || 'INR',
      workingHoursPerDay: current?.workingHoursPerDay?.toString() || '8',
      workingDaysPerMonth: current?.workingDaysPerMonth?.toString() || '26',
      overtimeRate: current?.overtimeRate?.toString() || '',
      undertimeDeductionRate: current?.undertimeDeductionRate?.toString() || '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: ''
    });
    setShowModal(true);
  };

  const openHistory = async (member: { id: string; userId: string; name: string }) => {
    setHistoryMember({ id: member.id, name: member.name });
    setShowHistoryModal(true);
    setIsLoadingHistory(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const response = await fetch(`${API_BASE_URL}/api/team-salaries?teamMemberId=${member.userId}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMemberHistory(data.data || []);
      }
    } catch {
      toast.error('Failed to load salary history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.baseSalary || !formData.effectiveFrom) {
      toast.error('Base salary and effective from date are required');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const payload: Record<string, unknown> = {
        userGymMappingId: formData.userGymMappingId,
        baseSalary: parseFloat(formData.baseSalary),
        currency: formData.currency,
        effectiveFrom: formData.effectiveFrom
      };
      if (formData.effectiveTo) payload.effectiveTo = formData.effectiveTo;
      if (formData.workingHoursPerDay) payload.workingHoursPerDay = parseFloat(formData.workingHoursPerDay);
      if (formData.workingDaysPerMonth) payload.workingDaysPerMonth = parseFloat(formData.workingDaysPerMonth);
      if (formData.overtimeRate) payload.overtimeRate = parseFloat(formData.overtimeRate);
      if (formData.undertimeDeductionRate) payload.undertimeDeductionRate = parseFloat(formData.undertimeDeductionRate);

      const response = await fetch(`${API_BASE_URL}/api/team-salaries`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || err.error || 'Failed to save salary');
      }

      toast.success(`Salary set for ${editingMember?.name}`);
      setShowModal(false);
      fetchSalaries();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to save salary');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentSalary = (gymMappingId: string): SalaryRecord | null => {
    const memberSalaries = salaries.filter(s => {
      const memberId = s.teamMember.id;
      const member = teamMembers.find(m => m.id === gymMappingId);
      return member && memberId === member.userId;
    });
    return memberSalaries[0] || null;
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {teamMembers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-5xl mb-4">💵</div>
            <p className="text-gray-500 font-medium">No team members found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Base Salary</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Working Hours</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Effective From</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {teamMembers.map(member => {
                  const current = getCurrentSalary(member.id);
                  return (
                    <tr key={member.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {(member.displayName || member.name).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{member.displayName || member.name}</p>
                            {member.displayName && member.name !== member.displayName && (
                              <p className="text-xs text-gray-400">{member.name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-700'}`}>
                          {ROLE_LABELS[member.role] || member.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {current ? (
                          <span className="font-semibold text-gray-900 text-sm">
                            {formatCurrency(current.baseSalary, current.currency)}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm italic">Not set</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {current?.workingHoursPerDay ? (
                          <span className="text-sm text-gray-600">
                            {current.workingHoursPerDay}h/day · {current.workingDaysPerMonth || '—'} days/mo
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {current ? (
                          <span className="text-sm text-gray-600">{formatDate(current.effectiveFrom)}</span>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {current && (
                            <button
                              onClick={() => openHistory(member)}
                              className="text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              History
                            </button>
                          )}
                          <button
                            onClick={() => openSetSalary(member)}
                            className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                              current
                                ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                          >
                            {current ? 'Update' : 'Set Salary'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Set/Update Salary Modal */}
      {showModal && editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {getCurrentSalary(editingMember.id) ? 'Update Salary' : 'Set Salary'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">{editingMember.name}</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Base Salary <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={formData.baseSalary}
                    onChange={e => setFormData(p => ({ ...p, baseSalary: e.target.value }))}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={e => setFormData(p => ({ ...p, currency: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Working Hours / Day</label>
                  <input
                    type="number"
                    value={formData.workingHoursPerDay}
                    onChange={e => setFormData(p => ({ ...p, workingHoursPerDay: e.target.value }))}
                    min="0"
                    step="0.5"
                    placeholder="8"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Working Days / Month</label>
                  <input
                    type="number"
                    value={formData.workingDaysPerMonth}
                    onChange={e => setFormData(p => ({ ...p, workingDaysPerMonth: e.target.value }))}
                    min="0"
                    step="0.5"
                    placeholder="26"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Overtime Rate / Hour</label>
                  <input
                    type="number"
                    value={formData.overtimeRate}
                    onChange={e => setFormData(p => ({ ...p, overtimeRate: e.target.value }))}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Undertime Deduction / Hour</label>
                  <input
                    type="number"
                    value={formData.undertimeDeductionRate}
                    onChange={e => setFormData(p => ({ ...p, undertimeDeductionRate: e.target.value }))}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Effective From <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={formData.effectiveFrom}
                    onChange={e => setFormData(p => ({ ...p, effectiveFrom: e.target.value }))}
                    required
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Effective To <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="date"
                    value={formData.effectiveTo}
                    onChange={e => setFormData(p => ({ ...p, effectiveTo: e.target.value }))}
                    min={formData.effectiveFrom}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-400 bg-gray-50 rounded-xl p-3">
                A new salary record will be created. Existing records are kept for payslip history.
              </p>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={isSubmitting}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-xl font-medium transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving…
                    </>
                  ) : 'Save Salary'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Salary History Modal */}
      {showHistoryModal && historyMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Salary History</h3>
                <p className="text-sm text-gray-500 mt-0.5">{historyMember.name}</p>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {isLoadingHistory ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : memberHistory.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No salary records found</p>
              ) : (
                <div className="space-y-3">
                  {memberHistory.map((record, i) => (
                    <div key={record.id} className={`rounded-xl border p-4 ${i === 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-100 bg-gray-50'}`}>
                      {i === 0 && (
                        <span className="inline-block text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full mb-2">Current</span>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(record.baseSalary, record.currency)}
                        </span>
                        <span className="text-xs text-gray-500">{record.currency}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                        <span>Effective: {formatDate(record.effectiveFrom)}</span>
                        {record.effectiveTo && <span>Until: {formatDate(record.effectiveTo)}</span>}
                        {record.workingHoursPerDay && <span>{record.workingHoursPerDay}h/day</span>}
                        {record.workingDaysPerMonth && <span>{record.workingDaysPerMonth} days/month</span>}
                        {record.overtimeRate ? <span>OT: {record.overtimeRate}/hr</span> : null}
                        {record.undertimeDeductionRate ? <span>UT deduction: {record.undertimeDeductionRate}/hr</span> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
